import React from 'react'
import { Button, Grid, GridItem } from '@deal/components'
import { Link } from 'react-router-dom'
import {
  SellableBrandCategoryComponent,
  PathSlugForCategoryComponent
} from '#src/generated/graphql'
import {
  generateCreateUrlFromCategory,
  generateCreateUrl,
  getOnboardingPathSlug
} from '#src/services/path'
import styles from './styles.css'

interface EmbeddedLandingPageProps {
  sellableId?: string
}

interface EmbeddedLandingPageContentProps {
  pathCategorySlug?: string | null
  header: React.ReactNode
  bullets: {
    header: React.ReactNode
    body: React.ReactNode
  }[]
}

const EmbeddedLandingPageContent: React.SFC<EmbeddedLandingPageContentProps> = ({
  header,
  bullets,
  pathCategorySlug
}) => {
  return (
    <div className={styles.wrapper}>
      <Grid>
        <GridItem col={2}>
          <div className={styles.header}>{header}</div>
          <div className={styles.bullets}>
            {bullets.slice(0, 3).map((bullet, idx) => (
              <div className={styles.bullet} key={idx}>
                <div className={styles.number}>{idx + 1}</div>
                <div className={styles.copy}>
                  <div className={styles.bulletHeader}>{bullet.header}</div>
                  <div className={styles.bulletCopy}>{bullet.body}</div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.actions}>
            {pathCategorySlug ? (
              <Link to={generateCreateUrlFromCategory(pathCategorySlug)}>
                <Button className={styles.submit}>Get free expert advice</Button>
              </Link>
            ) : (
              <Link to={generateCreateUrl(getOnboardingPathSlug())}>
                <Button className={styles.submit}>Get free expert advice</Button>
              </Link>
            )}
          </div>
        </GridItem>
      </Grid>
    </div>
  )
}

const EmbeddedLandingPage: React.SFC<EmbeddedLandingPageProps> = ({ sellableId }) => {
  if (!sellableId) {
    return null
  }

  return (
    <SellableBrandCategoryComponent
      variables={{
        friendlyIdOrId: sellableId
      }}
    >
      {({ data }) => {
        if (data && data.findSellable && data.findSellable.categories.length) {
          const sellableCategory = data.findSellable.categories[0]

          return (
            <PathSlugForCategoryComponent
              variables={{
                slug: sellableCategory.slug
              }}
            >
              {({ data: pathData }) => {
                if (
                  pathData &&
                  pathData.categoryBySlug &&
                  pathData.categoryBySlug.ancestorWithPath
                ) {
                  const pathCategorySlug = pathData.categoryBySlug.ancestorWithPath.slug

                  switch (sellableCategory.slug) {
                    case 'golf-clubs':
                    case 'golf-bags':
                    case 'golf-gloves':
                    case 'golf-balls':
                    case 'golf-rangefinders':
                    case 'golf-tees':
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Play your best round with free expert advice'}
                          bullets={[
                            {
                              header: (
                                <>
                                  Tell us what kind of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  you're looking for
                                </>
                              ),
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to a golf expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: (
                                <>
                                  Get a personalized list of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  recommendations
                                </>
                              ),
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )

                    case 'electric-bikes':
                    case 'mountain-bikes':
                    case 'bike-helmets':
                    case 'bike-locks':
                    case 'bike-pumps':
                    case 'bike-tire-repair':
                    case 'bike-mirrors':
                    case 'bike-cleats':
                    case 'bike-frames':
                    case 'bike-pedals':
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Have a better bike ride with free expert advice'}
                          bullets={[
                            {
                              header: (
                                <>
                                  Tell us what kind of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  you're looking for
                                </>
                              ),
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to a bike expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: (
                                <>
                                  Get a personalized list of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  recommendations
                                </>
                              ),
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )

                    case 'skis':
                    case 'ski-boots':
                    case 'ski-bindings':
                    case 'ski-poles':
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Fly down the mountain with free expert advice'}
                          bullets={[
                            {
                              header: (
                                <>
                                  Tell us what kind of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.pluralDisplayName}
                                  </span>{' '}
                                  you're looking for
                                </>
                              ),
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to a ski expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: (
                                <>
                                  Get a personalized list of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.pluralDisplayName}
                                  </span>{' '}
                                  recommendations
                                </>
                              ),
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )

                    case 'snowboards':
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Fly down the mountain with free expert advice'}
                          bullets={[
                            {
                              header: (
                                <>
                                  Tell us what kind of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  you're looking for
                                </>
                              ),
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to a snowboard expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: (
                                <>
                                  Get a personalized list of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  recommendations
                                </>
                              ),
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )

                    case 'winter-sports-jackets':
                    case 'winter-sports-gloves':
                    case 'winter-sports-pants':
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Fly down the mountain in style with free expert advice'}
                          bullets={[
                            {
                              header: (
                                <>
                                  Tell us what kind of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  you're looking for
                                </>
                              ),
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to a winter sports expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: (
                                <>
                                  Get a personalized list of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  recommendations
                                </>
                              ),
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )

                    case 'wedding-rings':
                    case 'engagement-rings':
                    case 'diamonds':
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Find her the perfect ring with free expert advice'}
                          bullets={[
                            {
                              header: (
                                <>
                                  Tell us what kind of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  you're looking for
                                </>
                              ),
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to a jewelry expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: (
                                <>
                                  Get a personalized list of{' '}
                                  <span className={styles.lowercase}>
                                    {sellableCategory.singularDisplayName}
                                  </span>{' '}
                                  recommendations
                                </>
                              ),
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )

                    default:
                      return (
                        <EmbeddedLandingPageContent
                          pathCategorySlug={pathCategorySlug}
                          header={'Shop with an expert. Love what you buy.'}
                          bullets={[
                            {
                              header: "Tell us what you're looking for",
                              body: 'Start with a 2 minute quiz'
                            },
                            {
                              header: 'Talk to an expert',
                              body: 'Ask them everything you want to know.'
                            },
                            {
                              header: 'Get a personalized list of recommendations',
                              body: "Your expert won't quit til you're happy"
                            }
                          ]}
                        />
                      )
                  }
                }
                return null
              }}
            </PathSlugForCategoryComponent>
          )
        }

        return null
      }}
    </SellableBrandCategoryComponent>
  )
}

export default EmbeddedLandingPage
